import { project, toPoints } from '../geometry';
import type { Camera, Vec2, Vec3 } from '../geometry';

/**
 * Small helpers for drawing the scenes from 3-D coordinates (metres) with the scene's own camera, so
 * the drawn art and the stone planes always agree on perspective.
 */

/** `points` attribute for a polygon given in world coordinates. */
export function worldPoints(camera: Camera, points: readonly Vec3[]): string {
  return toPoints(points.map((point) => project(camera, point)));
}

/** Screen positions of world points. */
export function screenPoints(camera: Camera, points: readonly Vec3[]): Vec2[] {
  return points.map((point) => project(camera, point));
}

export interface Box {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
  /** Near face (towards the camera). */
  z0: number;
  /** Far face. */
  z1: number;
}

export interface BoxFaces {
  front: Vec2[];
  /** Faces the eye can see; `null` when the face points away. */
  top: Vec2[] | null;
  bottom: Vec2[] | null;
  left: Vec2[] | null;
  right: Vec2[] | null;
}

/** The visible faces of an axis-aligned box, as screen polygons. */
export function boxFaces(camera: Camera, box: Box): BoxFaces {
  const { x0, x1, y0, y1, z0, z1 } = box;
  const p = (x: number, y: number, z: number): Vec2 => project(camera, [x, y, z]);
  const [ex, ey] = camera.eye;
  return {
    front: [p(x0, y1, z0), p(x1, y1, z0), p(x1, y0, z0), p(x0, y0, z0)],
    top: ey > y1 ? [p(x0, y1, z1), p(x1, y1, z1), p(x1, y1, z0), p(x0, y1, z0)] : null,
    bottom: ey < y0 ? [p(x0, y0, z0), p(x1, y0, z0), p(x1, y0, z1), p(x0, y0, z1)] : null,
    left: ex < x0 ? [p(x0, y1, z1), p(x0, y1, z0), p(x0, y0, z0), p(x0, y0, z1)] : null,
    right: ex > x1 ? [p(x1, y1, z0), p(x1, y1, z1), p(x1, y0, z1), p(x1, y0, z0)] : null,
  };
}

/** Axis-aligned bounds of screen points. */
export function boundsOf(points: readonly Vec2[]): {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
} {
  const xs = points.map((point) => point[0]);
  const ys = points.map((point) => point[1]);
  return { x0: Math.min(...xs), y0: Math.min(...ys), x1: Math.max(...xs), y1: Math.max(...ys) };
}

/** `a + (b - a) * t`. */
export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

/** Deterministic pseudo-random sequence (so drawn art never changes between renders). */
export function seeded(seed: number): () => number {
  let state = seed >>> 0 || 1;
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 4294967296;
  };
}

/**
 * Id helpers for a layer: every SVG id a layer defines is prefixed with the stage's unique id *and*
 * the layer's own name, so two layers of one scene can both define a `vignette` without colliding
 * (a duplicate id silently resolves to whichever comes first in the document).
 */
export function scope(
  ids: string,
  layer: string,
): { id: (name: string) => string; ref: (name: string) => string } {
  const id = (name: string): string => `${ids}-${layer}-${name}`;
  return { id, ref: (name: string): string => `url(#${id(name)})` };
}
